<template>
  <v-container v-if="!activeItem">
    <h3>Loading...</h3>
  </v-container>
  <v-container v-else>
    <h1 class="label-name">{{ activeItem.attributes.name }}</h1>
    <div v-if="isImageAnnotation">
      <h2 class="message">Full Image Review</h2>
    </div>
    <div v-else></div>
    <div class="canvas-wrapper">
      <canvas class="areaOfInterestCanvas" id="areaOfInterestCanvas"> </canvas>
    </div>
    <p>Image:</p>
    <div class="image-wrapper">
      <img
        class="image"
        id="image"
        crossorigin="anonymous"
        :src="activeItem.attributes.image_url"
        :key="activeItemKey"
        ref="image"
        @load="onImageLoad"
      />
      <Canvas
        class="canvas"
        id="canvas"
        v-if="showCanvas"
        :polygones="activeItemPolygones"
        :focalpoints="activeItemScreening"
        :image="$refs.image"
      />
    </div>
  </v-container>
</template>


<script>
import { mapActions, mapState, mapMutations } from 'vuex'

import Canvas from '@/components/review/canvas/Canvas'
import { cloneDeep } from 'lodash'
import rgba from '@/helpers/rgba'

var coordinates_x_factor; // used to scale x coordinates if image is resized compared to original
var coordinates_y_factor; // used to scale y coordinates if image is resized compared to original
export default {
  components: { Canvas, },
  data() {
    return {
      active: 0,
      showCanvas: false,
    }
  },
  computed: {
    activeItem() {
      let item = cloneDeep(this.data[this.active]);
      return item;
    },
    activeItemKey() {
      let key = this.active;
      return key;
    },
    isImageAnnotation(){
      return (this.activeItem.attributes.annotation_type === 'image_annotations' || this.activeItem.attributes.annotation_type === 'ImageAnnotation');
    },
    activeItemPolygones() {
      var coords = this.activeItem.attributes.coordinates
        .split(';')
        .map(str => str.split(','))
        .map(([x, y]) => ({ x, y }))
      var c;
      for (c of coords) {
        c['x'] = c['x']*coordinates_x_factor;
        c['y'] = c['y']*coordinates_y_factor;
      }
      return [{
        coords,
        color: rgba(),
      }];
    },
    activeItemScreening() {
      if (this.activeItem.attributes.radius == 0) { return [] }
      return [];
    },
    ...mapState({
      data: state => state.currentPage.data,
    }),
  },
  methods: {
    ...mapMutations('currentPage', ['prev']),
    ...mapActions('currentPage', ['get', 'submit', 'resetReviews']),

    async onImageLoad() {
      this.respond_to_annotation_type();
      this.set_coordinates_factors();
      this.showCanvas = true;
      this.drawAreaOfInterest();

    },
    respond_to_annotation_type(){
      // logic if annotation is image annotation
      if (this.isImageAnnotation){
        const img = document.getElementById('image');
        const img_width = img.naturalWidth;
        const img_height = img.naturalHeight;
        const orig_image_width = this.activeItem.attributes.orig_image_width;
        const orig_image_height = this.activeItem.attributes.orig_image_height;

        var c0,c1,c2,c3;

        if (img_width > img_height && orig_image_width > orig_image_height){
          c0 = '10.0,10.0';
          c1 = '10.0,' + (orig_image_height - 10).toString(10);
          c2 = (orig_image_width - 10).toString(10) + ',' + (orig_image_height - 10).toString(10);
          c3 = (orig_image_width - 10).toString(10) + ',' + '10.0';
                  }
        else if (img_width < img_height && orig_image_width < orig_image_height) {
          c0 = '10.0,10.0';
          c1 = '10.0,' + (orig_image_height - 10).toString(10);
          c2 = (orig_image_width - 10).toString(10) + ',' + (orig_image_height - 10).toString(10);
          c3 = (orig_image_width - 10).toString(10) + ',' + '10.0';
        }
        else{
          c0 = '10.0,10.0';
          c1 = '10.0,' + (orig_image_width - 10).toString(10);
          c2 = (orig_image_height - 10).toString(10) + ',' + (orig_image_width - 10).toString(10);
          c3 = (orig_image_height - 10).toString(10) + ',' + '10.0';
        }
        this.activeItem.attributes.coordinates = c0+';'+c1+';'+c2+';'+c3;
      }
    },
    async set_coordinates_factors(){
      // the image can be resized compared to original image & coordinates, which
      // needs to be addressed by a scaling factor on the coordinates
      var img = document.getElementById("image");
      const img_width = img.naturalWidth;
      const img_height = img.naturalHeight;
      const orig_image_width = this.activeItem.attributes.orig_image_width;
      const orig_image_height = this.activeItem.attributes.orig_image_height;

      if (img_width > img_height && orig_image_width > orig_image_height){
        coordinates_x_factor = img_width / orig_image_width;
        coordinates_y_factor = img_height / orig_image_height;
      }
      else if (img_width < img_height && orig_image_width < orig_image_height) {
        coordinates_x_factor = img_width / orig_image_width;
        coordinates_y_factor = img_height / orig_image_height;
      }
      else{
          coordinates_x_factor = img_width / orig_image_height;
          coordinates_y_factor = img_height / orig_image_width;
      }
    },
    drawAreaOfInterest() {
      var img = document.getElementById("image");

      var patch_canvas = document.getElementById("areaOfInterestCanvas");
      var patch_ctx = patch_canvas.getContext("2d");

      if (this.isImageAnnotation){
        return;
      }

      const coords = this.activeItem.attributes.coordinates
        .split(';')
        .map(str => str.split(','))
        .map(([x, y]) => ({ x, y }));

      var xs = [];
      var ys = [];
      var c;
      for (c of coords) {
        xs.push(c['x']*coordinates_x_factor);
        ys.push(c['y']*coordinates_y_factor);
      }
      var xmax = Math.max(...xs);
      var xmin = Math.min(...xs);
      var ymax = Math.max(...ys);
      var ymin = Math.min(...ys);
      var width = xmax - xmin;
      var height = ymax - ymin;
      var side_length = Math.max(Math.max(width, height), 100);

      var width_padding = (side_length - width) / 2;
      var height_padding = (side_length - height) / 2;

      // source images values
      var sx = Math.max(xmin-width_padding, 0);
      var sy = Math.max(ymin-height_padding, 0);
      var sWidth = width + 2*width_padding;
      var sHeight = height + 2*height_padding;

      // destination images values
      var dx = 0;
      var dy = 0;
      var dWidth = sWidth;
      var dHeight = sHeight;
      patch_canvas.height = dHeight;
      patch_canvas.width = dWidth;
      patch_ctx.drawImage(img, sx, sy, sWidth, sHeight, dx, dy, dWidth, dHeight);

    },
      async clearAreaOfInterest() {
        var canvas = document.getElementById("areaOfInterestCanvas");
        var ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        canvas.width = 0;
        canvas.height = 0;
    },
  },
  created() {
    this.get(this.$route.params.uuid);
  },
}
</script>


<style lang="scss" scoped>
.image-wrapper {
  position: relative;
}

.image {
  width: 100%;
  image-orientation: from-image;
}

.canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.canvas-wrapper {
  display: block;
  margin-left: auto;
  margin-right: auto;
  width: 100%;
  text-align: center;
}

.areaOfInterestCanvas {
  max-width: 50%;
  max-height: 50%;
}

.label-name {
  text-align: center;
}

.message {
  text-align: center;
}
</style>